import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin, Empty } from "antd";
import { XCircle, Download, CheckCircle2 } from "lucide-react";
import { IInvoice } from "../../../types/api/invoice";
import axios from "../../../lib/axios";
import dayjs from "dayjs";

export default function InvoicePublicPage() {
  const { token } = useParams<{ token: string }>();
  const [invoice, setInvoice] = useState<IInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`/public/invoices/${token}`)
      .then((res) => setInvoice(res.data.data))
      .catch((err) => setError(err.response?.data?.message || "Invoice not found or link expired."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50">
        <Spin size="large" tip="Loading invoice..." />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-sm text-center">
          <XCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Unavailable</h2>
          <p className="text-slate-500">{error || "This invoice could not be loaded."}</p>
        </div>
      </div>
    );
  }

  const fmt = (val: number | string | undefined) =>
    Number(val ?? 0).toLocaleString("en-GB", { style: "currency", currency: invoice.currency || "GBP" });

  const isPaid = invoice.status === "paid";
  const isOverdue = invoice.status === "overdue";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="text-sm text-blue-600 font-semibold mb-1">Invoice {invoice.invoice_number}</div>
            <h1 className="text-2xl font-bold text-slate-800">{invoice.title || invoice.invoice_number}</h1>
            <div className="text-sm text-slate-500 mt-1">
              Issued: {dayjs(invoice.issue_date).format("DD MMM YYYY")}
              <span className="ml-2 px-2 border-l border-slate-200">
                Due: {dayjs(invoice.due_date).format("DD MMM YYYY")}
              </span>
            </div>
          </div>
          {invoice.pdf_url && (
            <a href={invoice.pdf_url} target="_blank" rel="noreferrer">
              <button className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-700">
                <Download size={16} /> Download PDF
              </button>
            </a>
          )}
        </div>

        {/* STATUS BANNER */}
        {isPaid && (
          <div className="p-4 rounded-xl border flex items-center gap-3 bg-emerald-50 border-emerald-200 text-emerald-800">
            <CheckCircle2 size={24} className="text-emerald-500" />
            <div>
              <div className="font-semibold">Invoice Paid</div>
              <div className="text-sm opacity-90">Payment received. Thank you.</div>
            </div>
          </div>
        )}
        {isOverdue && (
          <div className="p-4 rounded-xl border flex items-center gap-3 bg-red-50 border-red-200 text-red-800">
            <XCircle size={24} className="text-red-500" />
            <div>
              <div className="font-semibold">Overdue</div>
              <div className="text-sm opacity-90">This invoice is past its due date. Please contact us.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LINE ITEMS */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
              <h2 className="text-lg font-semibold text-slate-800 mb-6">Invoice Items</h2>
              {invoice.line_items && invoice.line_items.length > 0 ? (
                <div className="space-y-4">
                  {invoice.line_items.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-start pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                      <div>
                        <div className="font-medium text-slate-800">{item.description}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {item.quantity} × {fmt(item.unit_price)} (VAT: {item.vat_rate}%)
                        </div>
                      </div>
                      <div className="font-semibold text-slate-800">{fmt(item.line_total)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="No line items." />
              )}

              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="space-y-3 max-w-xs ml-auto">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Subtotal</span>
                    <span>{fmt(invoice.subtotal)}</span>
                  </div>
                  {Number(invoice.discount_amount) > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Discount</span>
                      <span>-{fmt(invoice.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>VAT</span>
                    <span>{fmt(invoice.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-3 border-t border-slate-100">
                    <span>Total</span>
                    <span>{fmt(invoice.total)}</span>
                  </div>
                  {Number(invoice.amount_paid) > 0 && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Amount Paid</span>
                      <span>-{fmt(invoice.amount_paid)}</span>
                    </div>
                  )}
                  {Number(invoice.amount_outstanding) > 0 && (
                    <div className="flex justify-between text-base font-semibold text-red-700 pt-2 border-t border-slate-100">
                      <span>Outstanding</span>
                      <span>{fmt(invoice.amount_outstanding)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {(invoice.notes || invoice.terms) && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-6">
                {invoice.notes && (
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-2">Notes</h2>
                    <div className="text-slate-600 text-sm whitespace-pre-wrap">{invoice.notes}</div>
                  </div>
                )}
                {invoice.terms && (
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-2">Terms & Conditions</h2>
                    <div className="text-slate-500 text-xs whitespace-pre-wrap">{invoice.terms}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SUMMARY PANEL */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sticky top-6 space-y-4">
              <h2 className="text-lg font-semibold text-slate-800">Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <span className={`font-medium capitalize ${isPaid ? "text-emerald-600" : isOverdue ? "text-red-600" : "text-slate-700"}`}>
                    {invoice.status?.replace("_", " ")}
                  </span>
                </div>
                {invoice.po_reference && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">PO Reference</span>
                    <span className="font-medium text-slate-700">{invoice.po_reference}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Due Date</span>
                  <span className="font-medium text-slate-700">{dayjs(invoice.due_date).format("DD MMM YYYY")}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-slate-100">
                  <span className="text-slate-700 font-semibold">Total</span>
                  <span className="font-bold text-slate-900">{fmt(invoice.total)}</span>
                </div>
                {Number(invoice.amount_outstanding) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-red-600 font-semibold">Outstanding</span>
                    <span className="font-bold text-red-700">{fmt(invoice.amount_outstanding)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
